import { maybe, rd } from '@passionware/monads';
import { useLocation } from 'react-router-dom';
import {
  isOrganizationDashboardPath,
  shouldHoldMainSidebarOnOrgDashboard
} from 'v5/core/app-scope/organizationDashboardPath';
import { MainSidebar } from 'v5/features/core/MainSidebar';
import {
  EnterpriseMainSidebar,
  EnterpriseMainSidebarProps
} from 'v5/features/core/EnterpriseMainSidebar';
import { EnterpriseMainSidebarLoadingShell } from 'v5/features/core/enterprise/EnterpriseMainSidebarLoadingShell';
import { SettingsSidebar } from 'v5/features/core/SettingsSidebar';
import {
  EnterpriseSettingsSidebar,
  EnterpriseSettingsSidebarProps
} from 'v5/features/core/EnterpriseSettingsSidebar';
import { WithFeatureService } from 'v5/services/front/FeatureService/FeatureService';
import { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import { WithServices } from 'v5/platform/ts/services';

export type AppSettingsSidebarProps = EnterpriseSettingsSidebarProps &
  WithServices<[WithFeatureService, WithAppScopeService]>;

export function AppMainSidebar(props: EnterpriseMainSidebarProps) {
  const { pathname } = useLocation();
  const enterpriseRd = props.services.featureService.useFeature(
    props.services.appScopeService.useOrganizationScopeIdentity(),
    'enterprise'
  );
  const isEnterprise = rd.getOrElse(enterpriseRd, false);
  const organizationId =
    props.services.appScopeService.useMaybeOrganizationId();
  const workspacesRd = props.services.enterpriseService.useWorkspaces(
    isOrganizationDashboardPath(pathname) && isEnterprise
      ? organizationId
      : maybe.ofAbsent(),
    'active'
  );

  if (
    shouldHoldMainSidebarOnOrgDashboard(pathname, workspacesRd, enterpriseRd)
  ) {
    return <EnterpriseMainSidebarLoadingShell className={props.className} />;
  }

  if (isEnterprise) {
    return (
      <EnterpriseMainSidebar
        services={props.services}
        className={props.className}
      />
    );
  }

  return <MainSidebar {...props} />;
}

export function AppSettingsSidebar(props: AppSettingsSidebarProps) {
  const isEnterprise = rd.getOrElse(
    props.services.featureService.useFeature(
      props.services.appScopeService.useOrganizationScopeIdentity(),
      'enterprise'
    ),
    false
  );

  if (isEnterprise) {
    return (
      <EnterpriseSettingsSidebar
        services={props.services}
        className={props.className}
      />
    );
  }

  return <SettingsSidebar {...props} />;
}
